@if (node) {
  <div class="absolute top-0 right-0 h-full w-96 bg-white dark:bg-gray-900 shadow-2xl z-20 flex flex-col transform transition-transform duration-300 ease-in-out">
    <!-- Header -->
    <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
        {{ cleanLabel }}
      </h3>
      <div>
        <!-- Edit (pencil) button only shows for Chunks -->
        @if (isChunkNode && !isEditing) {
          <button
            (click)="setIsEditing(true)"
            class="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
            title="Edit this chunk directly">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
            </svg>
          </button>
        }
        <button
          (click)="close.emit()"
          class="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 ml-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 p-4 overflow-y-auto">
      <!-- Properties Section -->
      <div>
        <div class="space-y-2 text-sm">
          @if (propertyEntries.length === 0) {
            <p class="text-sm text-gray-500 dark:text-gray-400 italic">No properties to display.</p>
          }

          @for (entry of propertyEntries; track entry.key) {
            <div class="bg-gray-100 dark:bg-gray-800 p-2 rounded-md break-words">
              <p class="font-mono text-xs text-gray-500 dark:text-gray-400">{{ entry.formattedKey }}</p>

              <!-- Editable textarea ONLY for 'value' on CHUNK nodes -->
              @if (isEditing && isChunkNode && entry.key === 'value') {
                <textarea
                  [(ngModel)]="editedValue"
                  (ngModelChange)="computeIsConfirmDisabled()"
                  class="w-full mt-1 p-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-blue-300 dark:border-blue-700 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  rows="3"
                  autosize>
                </textarea>
              } @else {
                <p class="font-semibold text-gray-900 dark:text-gray-100 whitespace-pre-line">{{ entry.formattedValue }}</p>
              }
            </div>
          }
        </div>

        <!-- Knowledge Node Suggestions -->
        @if (isKnowledgeNode) {
          <div class="mt-4 flex justify-center">
            @if (!isEditing) {
              <button
                (click)="setIsEditing(true)"
                class="gap-2 px-3 py-1 text-sm font-medium bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 cursor-pointer text-[10px]">
                 See something wrong ? Click here to share your thoughts.
              </button>
            } @else {
              <div class="w-full">
                <label for="desired-change" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  What's wrong? Describe what you want to change:
                </label>
                <textarea
                  id="desired-change"
                  [(ngModel)]="desiredChange"
                  (ngModelChange)="computeIsConfirmDisabled()"
                  placeholder="e.g., This fact is outdated, the capital is now..."
                  class="w-full mt-1 p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm"
                  rows="3"
                  autosize>
                </textarea>
              </div>
            }
          </div>
        }
      </div>

      <!-- Change History (Chunks Only) -->
      @if (isChunkNode) {
        <div class="mt-6">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <h4 class="font-semibold text-gray-600 dark:text-gray-300">Change History</h4>
            </div>
          </div>

          @if (isHistoryLoading) {
            <p class="text-sm text-gray-500 dark:text-gray-400 animate-pulse">Loading history...</p>
          } @else if (history.length === 0) {
            <p class="text-sm text-gray-500 dark:text-gray-400 italic">No version history found.</p>
          } @else if (history.length > 0) {
            <div class="space-y-2 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden mt-2">
              @for (version of history; track $index; let i = $index; let first = $first) {
                <div class="border-b border-gray-200 dark:border-gray-700 last:border-b-0 mb-0"
                     [class.bg-green-50]="first"
                     [class.dark:bg-green-900/20]="first"
                     [class.bg-white]="!first"
                     [class.dark:bg-gray-900]="!first">
                  <button
                    (click)="toggleAccordion(i)"
                    class="w-full flex items-center justify-between p-3 text-left">
                    <div class="flex items-center gap-2">
                      <span class="text-xs font-mono text-gray-500 dark:text-gray-400 mr-2">V{{ history.length - i }}</span>
                      @if (first) {
                        <span class="text-xs font-semibold text-green-800 dark:text-green-200 bg-green-200 dark:bg-green-900/60 px-2 py-0.5 rounded-full">Current</span>
                      }
                      <span class="text-sm font-medium text-gray-700 dark:text-gray-200">{{ formatDate(version.timestamp) }}</span>
                    </div>
                    <svg class="w-5 h-5 transition-transform text-gray-500 dark:text-gray-400"
                         [class.rotate-180]="openAccordion === i"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>

                  @if (openAccordion === i) {
                    <div class="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm">
                      <div class="space-y-2">
                        <p class="whitespace-pre-line text-red-700 dark:text-red-300">
                          <strong>Old Value:</strong> {{ version.oldValue ?? "N/A" }}
                        </p>
                        <p class="whitespace-pre-line text-green-700 dark:text-green-300">
                          <strong>New Value:</strong> {{ version.newValue ?? "N/A" }}
                        </p>
                        <p class="text-gray-800 dark:text-gray-200">
                          <strong>Updated By:</strong> {{ version.updatedBy || "N/A" }}
                        </p>
                        @if (version.batchHadFailures) {
                          <p class="text-xs text-orange-600 dark:text-orange-400 font-medium">
                            Note: Part of a batch with failures.
                          </p>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- Editing Footer -->
    @if (isEditing && canSuggestEdit) {
      <div class="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 flex flex-col gap-3">
        <div class="flex justify-end gap-3">
          <button
            (click)="cancelEdit()"
            data-testid="cancel-edit-button"
            class="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">
            Cancel
          </button>
          <button
            (click)="handleOpenConfirmDialog()"
            [disabled]="isConfirmDisabled"
            data-testid="confirm-edit-button"
            class="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
            @if (isChunkNode) {
              <span>Review</span>
            } @else {
              <span>Analyze</span>
            }
          </button>
        </div>
      </div>
    }
  </div>
}
